import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu';
import {
  Clock,
  Calendar,
  User,
  FileText,
  AlertTriangle,
  CheckCircle,
  Filter,
  RefreshCw,
  Trash2,
  MoreVertical
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ShiftLogHistory = () => {
  const { user } = useAuth();
  const [shiftLogs, setShiftLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [daysFilter, setDaysFilter] = useState('7');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showBulkClearDialog, setShowBulkClearDialog] = useState(false);
  const [bulkClearing, setBulkClearing] = useState(false);

  useEffect(() => {
    fetchShiftLogs();
  }, [daysFilter]);

  const fetchShiftLogs = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/shift-logs/all?days=${daysFilter}`);
      setShiftLogs(response.data.shift_logs);
    } catch (error) {
      console.error('Error fetching shift logs:', error);
      toast.error('Failed to load shift logs');
    } finally {
      setLoading(false);
    }
  };

  // Admin delete functions
  const handleDeleteShiftLog = async () => {
    if (!selectedLog) return;
    
    try {
      setDeleting(true);
      await axios.delete(`${API}/shift-logs/${selectedLog.id}`);
      toast.success(`Shift log deleted successfully`);
      
      // Remove from list
      setShiftLogs(prev => prev.filter(log => log.id !== selectedLog.id));
      setShowDeleteDialog(false);
      setSelectedLog(null);
    } catch (error) {
      console.error('Error deleting shift log:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete shift log');
    } finally {
      setDeleting(false);
    }
  };

  const handleBulkClearShiftLogs = async () => {
    try {
      setBulkClearing(true);
      const response = await axios.delete(`${API}/shift-logs/clear-all`);
      toast.success(`${response.data.total_cleared} shift logs cleared successfully`);
      
      // Clear the list
      setShiftLogs([]);
      setShowBulkClearDialog(false);
    } catch (error) {
      console.error('Error clearing shift logs:', error);
      toast.error(error.response?.data?.detail || 'Failed to clear shift logs');
    } finally {
      setBulkClearing(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      timeZone: 'America/Chicago',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      timeZone: 'America/Chicago',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatDuration = (hours) => {
    if (!hours || isNaN(hours)) return '0h 0m';
    const h = Math.floor(hours);
    const m = Math.floor((hours - h) * 60);
    return `${h}h ${m}m`;
  };

  const getShiftTypeColor = (shiftDate, clockInTime) => {
    if (!clockInTime) return 'bg-gray-100 text-gray-800';
    
    const date = new Date(clockInTime);
    const hour = date.getHours();
    
    if (hour >= 6 && hour < 14) return 'bg-orange-100 text-orange-800';
    if (hour >= 14 && hour < 22) return 'bg-blue-100 text-blue-800';
    return 'bg-purple-100 text-purple-800';
  };

  const getShiftTypeName = (clockInTime) => {
    if (!clockInTime) return 'Unknown';
    
    const date = new Date(clockInTime);
    const hour = date.getHours();
    
    if (hour >= 6 && hour < 14) return 'Morning';
    if (hour >= 14 && hour < 22) return 'Evening';
    return 'Night';
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Shift Log History</h1>
            <p className="text-gray-600">Review shift handoff notes and reports</p>
          </div>
        </div>
        
        <div className="grid grid-cols-1 gap-6">
          {[...Array(3)].map((_, i) => (
            <Card key={i} className="card-shadow">
              <CardContent className="p-6">
                <div className="skeleton h-32 w-full rounded"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center space-x-3">
            <FileText className="h-8 w-8 text-indigo-600" />
            <span>Shift Log History</span>
          </h1>
          <p className="text-gray-600 mt-1">Review shift handoff notes and reports from all staff</p>
        </div>
        
        <div className="mt-4 sm:mt-0 flex items-center space-x-3">
          <Select value={daysFilter} onValueChange={setDaysFilter}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1">Last 24 hours</SelectItem>
              <SelectItem value="3">Last 3 days</SelectItem>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="14">Last 14 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="365">All Time</SelectItem>
            </SelectContent>
          </Select>
          
          {/* Admin Controls */}
          {user?.role === 'admin' && shiftLogs.length > 0 && (
            <Button 
              onClick={() => setShowBulkClearDialog(true)}
              variant="outline" 
              size="sm"
              className="border-red-200 text-red-700 hover:bg-red-50"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Clear All
            </Button>
          )}
          
          <Button onClick={fetchShiftLogs} variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="card-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Shift Logs</p>
                <p className="text-3xl font-bold text-gray-900">{shiftLogs.length}</p>
              </div>
              <FileText className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="card-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Days Covered</p>
                <p className="text-3xl font-bold text-gray-900">
                  {daysFilter === '365' ? 'All Time' : daysFilter}
                </p>
              </div>
              <Calendar className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="card-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">With Incidents</p>
                <p className="text-3xl font-bold text-gray-900">
                  {shiftLogs.filter(log => log.incidents && log.incidents.trim()).length}
                </p>
              </div>
              <AlertTriangle className="h-8 w-8 text-orange-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Shift Logs */}
      <div className="space-y-6">
        {shiftLogs.length === 0 ? (
          <Card className="card-shadow">
            <CardContent className="p-12 text-center">
              <FileText className="h-16 w-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Shift Logs Found</h3>
              <p className="text-gray-600">No shift logs have been recorded in the selected time period.</p>
            </CardContent>
          </Card>
        ) : (
          shiftLogs.map((log) => (
            <Card key={log.id} className="card-shadow">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-indigo-100 rounded-full">
                      <User className="h-5 w-5 text-indigo-600" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{log.staff_name}</CardTitle>
                      <CardDescription className="flex items-center space-x-4">
                        <span>{formatDate(log.shift_date)} • {formatTime(log.clock_in_time)} - {formatTime(log.clock_out_time)}</span>
                        <Badge className={`px-2 py-1 ${getShiftTypeColor(log.shift_date, log.clock_in_time)}`}>
                          {getShiftTypeName(log.clock_in_time)} Shift
                        </Badge>
                        <span className="text-sm text-gray-500">
                          {formatDuration(log.hours_worked)} worked
                        </span>
                      </CardDescription>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    {log.resident_count && (
                      <Badge variant="outline" className="px-3 py-1">
                        {log.resident_count} residents
                      </Badge>
                    )}
                    
                    {/* Admin Delete Button */}
                    {user?.role === 'admin' && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem 
                            onClick={() => {
                              setSelectedLog(log);
                              setShowDeleteDialog(true);
                            }}
                            className="text-red-600 focus:text-red-600"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete Shift Log
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                </div>
              </CardHeader>
              
              <CardContent className="space-y-4">
                {log.shift_notes && (
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2 flex items-center">
                      <FileText className="h-4 w-4 mr-2 text-blue-600" />
                      General Notes
                    </h4>
                    <p className="text-gray-700 bg-gray-50 p-3 rounded-lg">{log.shift_notes}</p>
                  </div>
                )}
                
                {log.tasks_completed && (
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2 flex items-center">
                      <CheckCircle className="h-4 w-4 mr-2 text-green-600" />
                      Tasks Completed
                    </h4>
                    <p className="text-gray-700 bg-green-50 p-3 rounded-lg">{log.tasks_completed}</p>
                  </div>
                )}
                
                {log.incidents && (
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2 flex items-center">
                      <AlertTriangle className="h-4 w-4 mr-2 text-orange-600" />
                      Incidents/Events
                    </h4>
                    <p className="text-gray-700 bg-orange-50 p-3 rounded-lg border-l-4 border-orange-400">{log.incidents}</p>
                  </div>
                )}
                
                {log.issues_to_report && (
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2 flex items-center">
                      <AlertTriangle className="h-4 w-4 mr-2 text-red-600" />
                      Issues to Report
                    </h4>
                    <p className="text-gray-700 bg-red-50 p-3 rounded-lg border-l-4 border-red-400">{log.issues_to_report}</p>
                  </div>
                )}
                
                {!log.shift_notes && !log.tasks_completed && !log.incidents && !log.issues_to_report && (
                  <p className="text-gray-500 italic text-center py-4">No detailed notes provided for this shift.</p>
                )}
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Individual Delete Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Shift Log</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this shift log from {selectedLog?.staff_name} on {selectedLog ? formatDate(selectedLog.shift_date) : ''}? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowDeleteDialog(false)}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteShiftLog}
              disabled={deleting}
            >
              {deleting ? 'Deleting...' : 'Delete Log'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Clear Dialog */}
      <Dialog open={showBulkClearDialog} onOpenChange={setShowBulkClearDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Clear All Shift Logs</DialogTitle>
            <DialogDescription>
              Are you sure you want to clear all shift logs? This action cannot be undone and will permanently delete all {shiftLogs.length} shift log{shiftLogs.length !== 1 ? 's' : ''}.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowBulkClearDialog(false)}
              disabled={bulkClearing}
            >
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleBulkClearShiftLogs}
              disabled={bulkClearing}
            >
              {bulkClearing ? 'Clearing...' : 'Clear All Logs'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ShiftLogHistory;